import React, { useEffect, useState, useRef } from "react";
import { getAuth } from "firebase/auth";
import { useNotifications } from "../hooks/notificationsContext";
import useDisplayNames from "../utils/useDisplayNames";

export default function ComposeMessagePanel({
  replyTo = null,
  recipient,
  setRecipient,
  message,
  setMessage,
  onClearReply
}) {
  const [sending, setSending] = useState(false);
  const { addNotification } = useNotifications();
  const auth = getAuth();
  const recipientRef = useRef(null);

  const { players: allRecipients } = useDisplayNames();
  const [searchResults, setSearchResults] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const themeColors = {
    teal: '#50E3C2',
    border: 'rgba(80, 227, 194, 0.2)',
    bg: 'rgba(0, 5, 5, 0.4)',
    text: '#FFF',
    muted: '#8b949e'
  };

  const inputStyle = {
    width: '100%',
    background: themeColors.bg,
    border: `1px solid ${themeColors.border}`,
    borderRadius: '4px',
    color: themeColors.text,
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: '0.75rem',
    padding: '10px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease'
  };

  const labelStyle = {
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: '0.6rem',
    color: themeColors.teal,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '6px',
    display: 'block'
  };

  useEffect(() => {
    if (!replyTo) return;
    if (!recipient) {
      setRecipient(replyTo.recipient || replyTo.sender || "");
      recipientRef.current?.focus();
    }
    if (!message && replyTo.originalMessage) {
      setMessage(`> ${replyTo.originalMessage}\n\n`);
    }
  }, [replyTo, recipient, message, setRecipient, setMessage]);

useEffect(() => {
  const term = recipient.trim().toLowerCase();
  if (!term) {
    setSearchResults([]);
    setHighlightIndex(-1);
    return;
  }
  const matches = allRecipients.filter((p) =>
    p.displayName.toLowerCase().startsWith(term)
  );
  
  setSearchResults(matches.slice(0, 4)); 
  setHighlightIndex(matches.length ? 0 : -1);
}, [recipient, allRecipients]);

  const handleSend = async () => {
    if (!recipient.trim() || !message.trim()) {
      addNotification("Recipient and message cannot be empty");
      return;
    }
    const user = auth.currentUser;
    if (!user) return;
    setSending(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch("https://senddirectmessage-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ messageSendRecipient: recipient, messageSendMessage: message.trim() }),
      });
      if (!response.ok) throw new Error();
      addNotification("Message sent.");
      handleClear();
    } catch (err) {
      addNotification("Failed to send.", "error");
    } finally {
      setSending(false);
    }
  };

  const handleClear = () => {
    setRecipient("");
    setMessage("");
    setSearchResults([]);
    if (onClearReply) onClearReply();
  };

  const handleSelectRecipient = (name) => {
    setRecipient(name);
    setSearchResults([]);
    recipientRef.current?.focus();
  };

return (
  <div className="compose-message-panel" style={{ 
    display: 'flex', 
    flexDirection: 'column', 
    gap: '4px',
    padding: '8px 10px',
    background: 'rgba(13, 22, 21, 0.9)', // Slightly more opaque to cover text behind suggestions
    borderTop: `1px solid ${themeColors.teal}`,
    width: '100%',
    boxSizing: 'border-box',
    flexShrink: 0,
    position: 'relative',
  }}>
    
    {/* RECIPIENT INPUT */}
    <div style={{ position: "relative", width: '100%', display: 'flex', alignItems: 'center', gap: '8px' }}>
      <label style={{ ...labelStyle, marginBottom: 0, whiteSpace: 'nowrap', width: '50px' }}>
        TO:
      </label>
      <input
        type="text"
        style={{
          ...inputStyle,
          padding: '4px 8px',
          fontSize: '0.7rem',
          borderColor: searchResults.length > 0 ? themeColors.teal : 'rgba(80, 227, 194, 0.1)',
        }}
        value={recipient}
        onChange={(e) => setRecipient(e.target.value)}
        placeholder="USERNAME"
        ref={recipientRef}
        autoComplete="off"
      />
      
      {/* SUGGESTIONS POPUP - Now drops DOWN over the textarea */}
      {searchResults.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '58px',
          right: 0,
          zIndex: 9999,
          background: '#0d1615',
          border: `1px solid ${themeColors.teal}`,
          borderRadius: '4px',
          marginTop: '2px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.8)'
        }}>
          {searchResults.map((p, idx) => (
            <div
              key={p.id}
              onMouseDown={() => handleSelectRecipient(p.displayName)}
              style={{
                padding: '6px 10px',
                cursor: 'pointer',
                fontFamily: 'JetBrains Mono',
                fontSize: '0.65rem',
                color: highlightIndex === idx ? '#000' : themeColors.teal,
                backgroundColor: highlightIndex === idx ? themeColors.teal : 'transparent',
              }}
            >
              {p.displayName}
            </div>
          ))}
        </div>
      )}
    </div>

    {/* MESSAGE INPUT */}
    <div style={{ width: '100%' }}>
      <textarea
        style={{ 
          ...inputStyle, 
          padding: '6px 8px',
          minHeight: '80px',
          maxHeight: '120px',
          fontSize: '0.7rem',
          resize: 'none',
          lineHeight: '1.2',
        }}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="ENTER MESSAGE..."
      />
    </div>

{/* ACTION BUTTONS */}
<div style={{ display: "flex", gap: "6px", width: '100%', marginTop: '4px' }}>
  <button
    className="jetbrains-btn"
    style={{ 
      flex: 1, 
      padding: '4px', 
      fontSize: '0.6rem', 
      height: '24px',
      cursor: sending ? 'not-allowed' : 'pointer',
      background: 'rgba(80, 227, 194, 0.1)', 
      color: '#50e3c2',
      border: '1px solid rgba(80, 227, 194, 0.3)',
      fontFamily: 'JetBrains Mono, monospace',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
    onClick={handleSend}
    disabled={sending}
  >
    {sending ? "SENDING..." : "SEND MESSAGE"}
  </button>

  <button
    className="jetbrains-btn"
    style={{ 
      flex: 0.4, 
      padding: '4px', 
      fontSize: '0.6rem', 
      height: '24px',
      background: 'rgba(255, 85, 85, 0.1)', 
      color: '#ff5555',
      border: '1px solid rgba(255, 85, 85, 0.3)',
      fontFamily: 'JetBrains Mono, monospace',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
    onClick={handleClear}
  >
    CLEAR
  </button>
</div>
  </div>
);
}